/**
 * PersonAccessPanel (SETTINGS-ACCESS-01) — one person, everything about their
 * access, in one place:
 *   Login  — status, create (invite or a temporary password handed over in
 *            person), send reset, remove authenticator (lost phone), disable.
 *   Role + extras — the shared UserPermissionsEditor (role chips, Also allow /
 *            Never, everything they can do).
 *   Recent access events for this login.
 * The email actions wait for the invite switch (rollout); the no-email ones
 * work today so the tech beta can get logins.
 */
import { useEffect, useState } from "react";
import { Check, Copy, KeyRound, Loader2, Mail, Power, RotateCcw, ShieldOff, UserRound, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson } from "@/lib/orgSettings";
import { copyText } from "@/components/Copyable";
import { UserPermissionsEditor } from "../UserPermissionsEditor";
import { STATE_META, suggestPassword, useAccessAction, useAccessEvents, type Person, type RoleRow } from "./access";

const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function PersonAccessPanel({ person, roles, onClose }: { person: Person; roles: RoleRow[]; onClose: () => void }) {
  const { user: me } = useAuth();
  const { isAdmin } = usePermissions();
  const act = useAccessAction();
  const invitesLive = useOrgSettingJson<boolean>("team_invites_live", false).data ?? false;
  const login = person.login;
  const isSelf = !!login && login.user_id === me?.id;
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<"none" | "create" | "temp" | "confirm_mfa" | "confirm_off">("none");
  const [email, setEmail] = useState(login?.email ?? person.rosterEmail ?? "");
  const [roleKey, setRoleKey] = useState(login?.role_key ?? (person.skill && /disp/i.test(person.skill) ? "dispatcher" : "tech"));
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const events = useAccessEvents(login?.user_id ?? null).data ?? [];

  useEffect(() => { setMode("none"); setMsg(null); setErr(null); setShown(null); setEmail(login?.email ?? person.rosterEmail ?? ""); setRoleKey(login?.role_key ?? "tech"); }, [person.key, login?.email, login?.role_key, person.rosterEmail]);

  const run = (v: Parameters<typeof act.mutate>[0], okText: string | ((r: { removed?: number }) => string)) => {
    setMsg(null); setErr(null);
    act.mutate(v, {
      onSuccess: (r) => {
        if (r.blocked) { setMsg("Invites are held until rollout — nothing was sent."); return; }
        setMsg(typeof okText === "function" ? okText(r) : okText);
        setMode("none");
      },
      onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
    });
  };
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const state = STATE_META[person.state];

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start gap-3 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 ring-1 ring-hairline"><UserRound className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-slate-900">{person.name}</span>
            {isSelf && <span className="text-xs text-slate-400">(you)</span>}
            <span className={cn("rounded-full px-2 py-0.5 text-[10.5px] font-semibold", state.chip)}>{state.label}</span>
            {person.status?.mfa && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 ring-1 ring-emerald-200">Two-factor on</span>}
          </div>
          <div className="text-xs text-slate-500">
            {[person.dept, person.skill, person.empno ? `#${person.empno}` : null].filter(Boolean).join(" · ")}
            {login?.email ? ` · ${login.email}` : person.rosterEmail ? ` · ${person.rosterEmail} (ESC)` : ""}
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 text-slate-300 hover:bg-white hover:text-slate-600" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>

      <div className="space-y-4 p-4">
        {/* ---- Login ---- */}
        <section>
          <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><KeyRound className="h-3.5 w-3.5" /> Login</h4>
          {login ? (
            <div className="mt-1.5 space-y-2">
              <div className="text-xs text-slate-600">
                {person.status?.last_sign_in_at ? `Last signed in ${fmtWhen(person.status.last_sign_in_at)}` : person.state === "invited" ? `Invited ${fmtWhen(person.status?.invited_at ?? null)} — hasn't set a password yet` : "Has never signed in"}
                {person.status?.disabled && <span className="ml-1 font-semibold text-rose-700">· login is disabled</span>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <ActionButton icon={Mail} disabled={!invitesLive || act.isPending} title={invitesLive ? "Email a password-reset link" : "Held until rollout — use a temporary password instead"}
                  onClick={() => run({ action: "send_reset", email: login.email ?? "", empno: person.empno }, `Password reset sent to ${login.email}.`)}>Send password reset</ActionButton>
                {person.state === "invited" && (
                  <ActionButton icon={Mail} disabled={!invitesLive || act.isPending} title={invitesLive ? "Resend the set-password invite" : "Held until rollout"}
                    onClick={() => run({ action: "resend_invite", email: login.email ?? "", empno: person.empno, role_key: login.role_key }, `Invite re-sent to ${login.email}.`)}>Resend invite</ActionButton>
                )}
                {isAdmin && (
                  <ActionButton icon={RotateCcw} disabled={act.isPending} active={mode === "temp"} title="Set a temporary password you hand over in person — nothing is emailed"
                    onClick={() => { setMode(mode === "temp" ? "none" : "temp"); setPassword(suggestPassword()); setShown(null); }}>Temporary password</ActionButton>
                )}
                {isAdmin && person.status?.mfa && (
                  <ActionButton icon={ShieldOff} disabled={act.isPending} active={mode === "confirm_mfa"} title="Lost phone — remove their authenticator; they set it up again at the next sign-in"
                    onClick={() => setMode(mode === "confirm_mfa" ? "none" : "confirm_mfa")}>Remove authenticator</ActionButton>
                )}
                {isAdmin && !isSelf && (person.status?.disabled ? (
                  <ActionButton icon={Power} disabled={act.isPending} title="Turn the login back on"
                    onClick={() => run({ action: "enable_login", user_id: login.user_id, email: login.email ?? undefined }, "Login re-enabled.")}>Re-enable login</ActionButton>
                ) : (
                  <ActionButton icon={Power} disabled={act.isPending} active={mode === "confirm_off"} tone="danger" title="Turn the login off without deleting the person"
                    onClick={() => setMode(mode === "confirm_off" ? "none" : "confirm_off")}>Disable login</ActionButton>
                ))}
              </div>

              {mode === "temp" && (
                <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3">
                  <div className="text-xs font-semibold text-slate-800">Temporary password</div>
                  <p className="mt-0.5 text-[11px] text-slate-500">Hand it over in person or by phone. It shows once. They should change it after signing in (Sign in → your name → My sign-in).</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input value={password} onChange={(e) => setPassword(e.target.value)} className="h-8 min-w-[220px] flex-1 rounded-md border border-hairline bg-white px-2 font-mono text-sm text-slate-800" />
                    <button type="button" onClick={() => setPassword(suggestPassword())} className="text-xs font-medium text-brand-700 hover:underline">Suggest another</button>
                    <button type="button" disabled={password.length < 10 || act.isPending}
                      onClick={() => run({ action: "set_temp_password", user_id: login.user_id, email: login.email ?? undefined, password }, "Temporary password set — give it to them now.")}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50">
                      {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Set it
                    </button>
                  </div>
                  {password.length < 10 && <p className="mt-1 text-[11px] text-rose-600">At least 10 characters.</p>}
                </div>
              )}
              {mode === "confirm_mfa" && (
                <Confirm text="Remove their authenticator? Sign-in goes back to password only until they add one again."
                  busy={act.isPending} onYes={() => run({ action: "remove_mfa", user_id: login.user_id, email: login.email ?? undefined }, (r) => `Removed ${r.removed ?? 0} authenticator${r.removed === 1 ? "" : "s"}.`)} onNo={() => setMode("none")} />
              )}
              {mode === "confirm_off" && (
                <Confirm text={`Disable ${person.name}'s login? They can't sign in until you re-enable it. Nothing else changes.`} danger
                  busy={act.isPending} onYes={() => run({ action: "disable_login", user_id: login.user_id, email: login.email ?? undefined }, "Login disabled.")} onNo={() => setMode("none")} />
              )}
            </div>
          ) : (
            <div className="mt-1.5">
              {mode !== "create" ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500">No Pegasus login yet.</span>
                  <button type="button" onClick={() => setMode("create")} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800"><KeyRound className="h-3.5 w-3.5" /> Create a login</button>
                </div>
              ) : (
                <div className="rounded-lg border border-hairline bg-slate-50/60 p-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-medium text-slate-500">Email (their sign-in name)</span>
                      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@sedgwickheating.com"
                        className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-medium text-slate-500">Role</span>
                      <select value={roleKey} onChange={(e) => setRoleKey(e.target.value)} className="h-9 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700">
                        {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button type="button" disabled={!validEmail || !invitesLive || act.isPending} title={invitesLive ? "Create the login and email a set-password link" : "Held until rollout — use a temporary password"}
                      onClick={() => run({ action: "create_and_invite", email: email.trim(), empno: person.empno, role_key: roleKey }, `Invite sent to ${email.trim()}.`)}
                      className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                      <Mail className="h-3.5 w-3.5" /> Create + email an invite{!invitesLive ? " (held)" : ""}
                    </button>
                    {isAdmin && (
                      <button type="button" disabled={!validEmail || act.isPending} onClick={() => { const p = suggestPassword(); setPassword(p); setMode("temp"); setShown(null);
                        run({ action: "create_with_password", email: email.trim(), empno: person.empno, role_key: roleKey, password: p }, "Login created — give them this temporary password now.");
                        setShown(p); }}
                        className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50">
                        {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />} Create with a temporary password
                      </button>
                    )}
                    <button type="button" onClick={() => setMode("none")} className="text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
                  </div>
                  {!validEmail && email.trim() && <p className="mt-1 text-[11px] text-rose-600">That doesn't look like an email address.</p>}
                </div>
              )}
            </div>
          )}
          {shown && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
              <span className="text-xs font-semibold text-emerald-800">Temporary password:</span>
              <code className="rounded bg-white px-2 py-0.5 font-mono text-emerald-900 ring-1 ring-emerald-200">{shown}</code>
              <button type="button" onClick={() => { void copyText(shown).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 2000); }); }} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 hover:underline">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
              <span className="text-[11px] text-emerald-700">Shown once — it's gone when you close this.</span>
            </div>
          )}
          {msg && <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><Check className="h-3.5 w-3.5" /> {msg}</p>}
          {err && <p className="mt-2 text-xs font-medium text-rose-600">{err}</p>}
        </section>

        {/* ---- Role + extras ---- */}
        {login && (
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Role &amp; Extras</h4>
            <div className="mt-1.5"><UserPermissionsEditor userId={login.user_id} /></div>
          </section>
        )}

        {/* ---- Recent events ---- */}
        {login && events.length > 0 && (
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recent</h4>
            <ul className="mt-1 space-y-0.5 text-[11.5px] text-slate-500">
              {events.slice(0, 6).map((e) => (
                <li key={e.id}>{fmtWhen(e.created_at)} · {(e.actor_email ?? "someone").split("@")[0]} — {e.detail ?? e.action}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Card>
  );
}

function ActionButton({ icon: Icon, children, onClick, disabled, title, active, tone }: {
  icon: React.ComponentType<{ className?: string }>; children: React.ReactNode; onClick: () => void; disabled?: boolean; title?: string; active?: boolean; tone?: "danger";
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={cn("inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        active ? "border-slate-400 bg-slate-100 text-slate-800" : tone === "danger" ? "border-hairline bg-white text-rose-700 hover:bg-rose-50" : "border-hairline bg-white text-slate-700 hover:bg-slate-50")}>
      <Icon className="h-3.5 w-3.5" /> {children}
    </button>
  );
}

function Confirm({ text, onYes, onNo, busy, danger }: { text: string; onYes: () => void; onNo: () => void; busy: boolean; danger?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50 px-3 py-2 text-xs">
      <span className="text-slate-700">{text}</span>
      <button type="button" disabled={busy} onClick={onYes} className={cn("rounded-md px-3 py-1 font-semibold text-white disabled:opacity-50", danger ? "bg-rose-600 hover:bg-rose-700" : "bg-brand-700 hover:bg-brand-800")}>{busy ? "Working…" : "Yes"}</button>
      <button type="button" onClick={onNo} className="font-medium text-slate-500 hover:text-slate-800">No</button>
    </div>
  );
}
